import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { postResetPassword } from '@/auth/api';
import { FormTextField } from '@/components/form/form-text-field';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AuthCard } from '@/pages/auth/auth-card';
import { applyApiErrors } from '@/pages/auth/form-errors';
import type { LoginLocationState } from '@/pages/auth/login-state';
import { newPasswordSchema } from '@/pages/auth/password-rules';

const schema = z
  .object({
    password: newPasswordSchema,
    password_confirmation: z.string().min(1),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });

type ResetValues = z.infer<typeof schema>;

export default function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = params.get('token') ?? '';
  const email = params.get('email') ?? '';

  const form = useForm<ResetValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', password_confirmation: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await postResetPassword({
        token,
        email,
        password: values.password,
        password_confirmation: values.password_confirmation,
      });
      const state: LoginLocationState = { notice: 'passwordReset' };
      navigate('/login', { replace: true, state });
    } catch (error) {
      applyApiErrors(error, form.setError, (message) => toast.error(message));
    }
  });

  const linkInvalid = token.length === 0 || email.length === 0;

  return (
    <AuthCard
      title={t('resetPassword.title')}
      footer={
        <Link to="/login" className="text-dim hover:text-strong inline-flex items-center gap-1">
          <ArrowLeft className="size-3.5" aria-hidden />
          {t('forgotPassword.backToLogin')}
        </Link>
      }
    >
      {linkInvalid ? (
        <p className="font-mono text-sm text-destructive" role="alert">
          {t('verifyEmail.failed')}
        </p>
      ) : (
        <Form {...form}>
          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <p className="font-mono text-xs text-dim">{email}</p>
            <FormTextField
              control={form.control}
              name="password"
              label={t('fields.password')}
              type="password"
              autoComplete="new-password"
            />
            <FormTextField
              control={form.control}
              name="password_confirmation"
              label={t('fields.passwordConfirm')}
              type="password"
              autoComplete="new-password"
            />
            <Button type="submit" variant="primary" disabled={form.formState.isSubmitting}>
              {t('resetPassword.submit')}
            </Button>
          </form>
        </Form>
      )}
    </AuthCard>
  );
}
